// ============================================================
// exportCasePdf.ts
// Single-case PDF summary export. Builds a branded, print-ready HTML
// document and opens it in a new tab to print / save as PDF (no pdf
// library — plain HTML/CSS in its own document, so the app's styles
// don't leak in and vice versa).
//
// Branded to the approved export mockup (casevo-case-export-redesign.html
// §C, .paper :85-113):
//   • letterhead — Casevo mark + firm name (when letterhead is on) +
//     "Personal injury · Case summary", generated date/time + user on the right;
//   • the forest title band — type pill, case ref, STATUS pill, case name,
//     parties · matter number · fee earner, and 4 key facts (accident,
//     limitation, injuries, next deadline — the same facts as the case hero);
//   • the firm's document fonts (utils/documentFonts — DM Sans by default), section heads with a tick bar;
//   • readable dates everywhere ("14 Oct 2026", never raw ISO);
//   • deadlines as a compact dated list with an Open / Done / Limitation chip;
//   • footer: confidentiality line (firm + ref) · "Generated by Casevo",
//     page n of N in the page margin where the browser supports it.
// Unchanged: A4 @page 15mm, the summary/full field sets, hide-empty,
// letterhead, the dark-paper option, witnesses/deadlines, print via new tab.
// ============================================================
import type { Witness } from '../types/case.types'
import { documentTheme, type DocumentTheme } from './documentTheme'
import type { DocumentFontsCss } from './documentFonts'
import { clientDocumentFonts } from './documentFontFiles'
import type { CaseDeadline } from '../types/deadlines.types'
import { CASE_SECTION_GROUPS } from './caseSectionGroups'
import { caseName, caseRef as sharedCaseRef } from './caseName'
import { caseDobOf } from './caseDob'
import { textDateToIso } from './civilDate'
import { estimatedLimitation, estimateParen, noTimeLimitApplies } from './limitationRules'
import { claimTypeLabel } from './claimTypes'
import { claimFactsOf, claimOfRaw } from './caseClaim'
import { limitationRowOf } from '../components/case/caseFacts'

export interface CasePdfOptions {
  sections: Set<string>          // section keys to include (client/accident/…)
  detailLevel: 'summary' | 'full'
  hideEmpty: boolean
  letterhead: boolean
  dark: boolean                  // print-safe dark swap; light is default
  includeWitnesses: boolean
  includeDeadlines: boolean
  firmName?: string | null
  // The firm logo (data URI — GET /api/firm/logo). Printed top-left in place
  // of the initial mark when the letterhead is on; absent → as before.
  logoDataUri?: string | null
  // The firm's document branding (utils/documentTheme + documentFonts; null =
  // the default — Casevo green, no secondary, DM Sans). ALWAYS applied — the
  // letterhead toggle only shows / hides the letterhead block, never the
  // firm's colours + fonts.
  accentColour?: string | null
  secondaryColour?: string | null
  headingFont?: string | null
  bodyFont?: string | null
  witnesses?: Witness[]
  deadlines?: CaseDeadline[]
  // Who generated it (letterhead "by …"). Optional.
  generatedBy?: string | null
}

// ── value helpers ──────────────────────────────────────────
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// Rich-text (TipTap) fields are stored as HTML — medical/TPS notes, injury
// descriptions, witness statements, deadline notes. Convert to plain text
// (block ends → newlines; the value cell is pre-wrap) BEFORE escaping.
// DOMParser builds an inert document — no scripts run, no resources load.
const plainText = (s: string | null | undefined): string => {
  if (!s) return ''
  if (!/<\/?[a-z][^>]*>/i.test(s)) return s
  const withBreaks = s.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim()
}

// ── readable dates (was raw ISO, e.g. dueDate.slice(0,10) → "2026-10-14") ──
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/
// Date-only / ISO-instant string → "14 Oct 2026" (read from the string's own
// Y-M-D, so no timezone shift). Non-dates return null.
export const readableDate = (v: unknown): string | null => {
  if (typeof v !== 'string') return null
  const m = ISO_DATE.exec(v.trim())
  if (!m) return null
  const mo = Number(m[2])
  if (mo < 1 || mo > 12) return null
  return `${m[3]} ${MONTHS[mo - 1]} ${m[1]}`
}
const ymd = (v: unknown): string | null => {
  if (typeof v !== 'string') return null
  const m = ISO_DATE.exec(v.trim())
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null
}

// Format a raw value for display. boolean → Yes/No; ISO date → readable;
// array → '; '-joined; null/undefined → ''.
const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (Array.isArray(v)) return v.map((x) => (x === null || x === undefined ? '' : String(x))).join('; ')
  return readableDate(v) ?? String(v)
}

// "Empty" for the hide-empty option: null/undefined/''/false/[] all count as
// absent, so hiding empties on the Injuries section (all booleans) usefully
// collapses it to only the injuries that are present.
// ★ (Audit) `false` is only "empty" for the injury checkboxes (an unticked
// injury = not present). Elsewhere a stored false is a recorded "No" —
// seatbelt worn, police involved, claimant vehicle responsible, time off work
// — legally meaningful answers that hide-empty must not drop.
const isEmptyValue = (v: unknown, key = ''): boolean =>
  v === null || v === undefined || v === '' || (v === false && key.startsWith('injury')) || (Array.isArray(v) && v.length === 0)

// ── key facts (same rules as the case hero — components/case/caseFacts.ts) ──
const INJURY_SHORT: [string, string][] = [
  ['injuryNeck', 'Neck'], ['injuryShoulder', 'Shoulder'], ['injuryBack', 'Back'], ['injuryRibsChestTorso', 'Ribs/chest/torso'],
  ['injuryFaceCheckboneJawNose', 'Face/jaw/nose'], ['injuryForearmWristHandFingers', 'Arm/wrist/hand'],
  ['injuryDamageToTeeth', 'Teeth'], ['injuryHeadSenses', 'Head/senses'], ['injuryHipsPelvisGenitals', 'Hips/pelvis'],
  ['injuryLegKneeAnkleFootToe', 'Leg/knee/foot'], ['injuryPsychological', 'Psychological'],
  // B3 (B2c item 4)
  ['injuryHearingEars', 'Hearing/ears'], ['injuryLungsBreathing', 'Lungs/breathing'], ['injurySkin', 'Skin'],
  ['injuryInternalOrgans', 'Internal organs'], ['injuryFatal', 'Fatal'], ['injuryOther', 'Other'],
]

// ── palette (dark = palette swap over the same structure) ──
// Every accent colour comes from the document theme (utils/documentTheme) —
// the Casevo greens unless the firm set its own; the neutrals stay as they are.
// The divider rules take the secondary colour when one is set (else as before).
const palette = (dark: boolean, th: DocumentTheme) =>
  dark
    ? {
        bg: '#16201b', text: '#e7efe9', heading: th.dark.heading, muted: '#93a39a', strong: '#e7efe9',
        metaText: '#b7c4bc', lineSolid: '#38443d', lineDot: '#2c352f', foot: '#7f8d85', firm: '#e7efe9',
        bandFrom: th.dark.bandFrom, bandTo: th.dark.bandTo, chipBg: th.dark.chipBg, chipInk: th.dark.chipInk,
        limBg: 'rgba(243,160,148,0.16)', limInk: '#f3a094', doneBg: '#2c352f', doneInk: '#93a39a', note: '#b7c4bc',
        rule: th.dark.rule ?? th.dark.heading, secRule: th.dark.secRule ?? '#38443d', pillBg: th.dark.pillBg, pillInk: th.dark.pillInk,
      }
    : {
        bg: '#ffffff', text: '#1a2420', heading: th.heading, muted: '#7a8880', strong: '#1a2420',
        metaText: '#55635c', lineSolid: '#d8e0dc', lineDot: '#e3e9e6', foot: '#9aa8a0', firm: th.firmInk,
        bandFrom: th.bandFrom, bandTo: th.bandTo, chipBg: th.chipBg, chipInk: th.chipInk,
        limBg: '#fbe9e6', limInk: '#c23b2c', doneBg: '#eef2ef', doneInk: '#5b6b62', note: '#55635c',
        rule: th.hasSecondary ? th.rule : th.heading, secRule: th.hasSecondary ? th.secRule : '#d8e0dc', pillBg: th.pillBg, pillInk: th.pillInk,
      }

// Mockup .paper / .lh / .band / .facts / .sec / .kv / .dl / .pfoot (:85-113),
// print-adapted. SIZED IN pt FOR PRINT: the mockup's px values were drawn for a
// shrunken on-screen "paper" thumbnail, so used as-is they printed at ~5–8pt
// (10.5px body = 7.9pt, 7px labels = 5.25pt) — too small on a real A4 sheet.
// A4 at 15mm margins = 180mm × 267mm usable (≈ 510pt × 757pt); the .pdf column
// fills that full width (no max-width in print). Body 11pt, values 11pt,
// labels 10.5pt, title 19pt — comfortably readable on paper (bumped ~10% from
// the first pt pass: body 10 → 11pt).
// @media screen previews the same 180mm column on the on-screen tab.
// CSS string literal for @page margin-box content (quotes/backslashes/newlines escaped; `<` as \3C so it can never close the <style>).
const cssStr = (s: string): string => `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/</g, '\\3C ').replace(/[\r\n]+/g, ' ')}"`

// PAGE FURNITURE (Chrome page-margin boxes — @page :first + margin boxes):
//   • page 1   — the full letterhead + green title band (in the document flow);
//                its top margin box is left EMPTY (@page :first).
//   • pages 2+ — a slim RUNNING HEADER in the top margin: firm (or Casevo) ·
//                case ref on the left, the case name on the right — so every
//                continuation page is identifiable on its own if separated.
//   • EVERY page — the footer in the bottom margin: "Confidential — prepared
//                for {firm} · {ref}" left, "Generated by Casevo · Page n of N"
//                right. (Previously only the page number repeated; the
//                confidential line printed once, at the end of the last page.)
interface Furniture { headLeft: string; headRight: string; footLeft: string }
const styleBlock = (dark: boolean, f: Furniture, th: DocumentTheme, fonts: DocumentFontsCss): string => {
  const c = palette(dark, th)
  const box = `font: 8.5pt ${fonts.body}; color: ${c.foot};`
  const head = `font: 600 8.5pt ${fonts.body}; color: ${c.metaText}; vertical-align: bottom; padding-bottom: 2.5mm; margin-bottom: 3mm; border-bottom: .75pt solid ${c.lineSolid};`
  return `
    ${fonts.faces}
    @page { size: A4; margin: 21mm 15mm 18mm;
      @top-left { content: ${cssStr(f.headLeft)}; ${head} }
      @top-right { content: ${cssStr(f.headRight)}; ${head} text-align: right; }
      @bottom-left { content: ${cssStr(f.footLeft)}; ${box} vertical-align: top; padding-top: 5mm; }
      @bottom-right { content: "Generated by Casevo · Page " counter(page) " of " counter(pages); ${box} vertical-align: top; padding-top: 5mm; text-align: right; } }
    @page :first { margin-top: 15mm; @top-left { content: none; border: 0; } @top-right { content: none; border: 0; } }
    @media print { .pfoot { display: none !important; } }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; width: auto; }
    body { font-family: ${fonts.body};
           background: ${c.bg}; color: ${c.text}; font-size: 11pt; line-height: 1.45;
           -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @media screen { body { padding: 15mm; } .pdf { max-width: 180mm; margin: 0 auto; } }
    .pdf { width: 100%; }
    .lh { display: flex; align-items: center; gap: 11pt; padding-bottom: 11pt; border-bottom: 1.5pt solid ${c.rule}; }
    .logo { display: block; flex: none; max-height: 45pt; max-width: 170pt; width: auto; height: auto; object-fit: contain; }
    .mark { width: 33pt; height: 33pt; border-radius: 8pt; background: linear-gradient(135deg, ${th.bandFrom}, ${th.bandTo});
            display: grid; place-items: center; color: ${th.onAccent}; font-family: ${fonts.heading}; font-weight: 800; font-size: 15pt; flex: none; }
    .firm { font-family: ${fonts.heading}; font-weight: 800; font-size: 16.5pt; line-height: 1.2; color: ${c.firm}; letter-spacing: -.01em; }
    .firm small { display: block; font-family: ${fonts.body}; font-weight: 500; font-size: 8pt; color: ${c.muted}; letter-spacing: .12em; text-transform: uppercase; margin-top: 2pt; }
    .gen { margin-left: auto; text-align: right; font-size: 9pt; color: ${c.metaText}; line-height: 1.5; }
    .gen b { color: ${c.strong}; }
    .band { margin-top: 13pt; border-radius: 9pt; background: linear-gradient(135deg, ${c.bandFrom}, ${c.bandTo}); color: ${th.onAccent};
            padding: 13pt 15pt; break-inside: avoid; }
    .pills { display: flex; gap: 5pt; margin-bottom: 5pt; }
    .pills span { font-size: 7.5pt; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; background: ${c.pillBg}; color: ${c.pillInk};
                  border-radius: 3pt; padding: 1.5pt 5pt; }
    .pills span.m { font-variant-numeric: tabular-nums; letter-spacing: .02em; text-transform: none; } /* the reference — body font (bundled), not a system monospace, so server + print match */
    .band h1 { font-family: ${fonts.heading}; font-size: 19pt; line-height: 1.2; margin: 0; font-weight: 700; letter-spacing: -.01em; }
    .band .sub { font-size: 10.5pt; color: rgba(${th.onAccentRgb},.85); margin-top: 2pt; }
    .facts { display: grid; grid-template-columns: repeat(4, 1fr); gap: 7pt; margin-top: 10pt; }
    .facts > div { background: rgba(${th.onAccentRgb},.1); border: .75pt solid rgba(${th.onAccentRgb},.14); border-radius: 5pt; padding: 5.5pt 8pt; }
    .facts .k { font-size: 7pt; letter-spacing: .14em; text-transform: uppercase; opacity: .75; font-weight: 700; }
    .facts .v { font-size: 11.5pt; font-weight: 700; margin-top: 1pt; }
    .facts .e { font-weight: 500; opacity: .75; font-size: 9pt; }
    .sec { margin-top: 15pt; }
    .sec-h { display: flex; align-items: center; gap: 6pt; font-family: ${fonts.heading}; font-size: 9.5pt; font-weight: 700; letter-spacing: .16em; text-transform: uppercase;
             color: ${c.heading}; padding-bottom: 4pt; border-bottom: .75pt solid ${c.secRule}; margin-bottom: 4pt; break-after: avoid; }
    .sec-h i { width: 3pt; height: 10pt; border-radius: 1.5pt; background: ${c.heading}; display: inline-block; }
    .kv { display: grid; grid-template-columns: 1fr 1fr; column-gap: 24pt; }
    .kv + .kv { margin-top: 8pt; }
    .row { display: flex; justify-content: space-between; gap: 8pt; padding: 3.5pt 0; border-bottom: .75pt dotted ${c.lineDot}; break-inside: avoid; }
    .row .k { color: ${c.muted}; font-size: 10.5pt; }
    .row .v { font-weight: 600; font-size: 11pt; text-align: right; word-break: break-word; }
    .row.full { grid-column: 1 / -1; display: block; }
    .row.full .v { text-align: left; font-weight: 500; display: block; margin-top: 1.5pt; color: ${c.note}; white-space: pre-wrap; }
    .dl { display: grid; grid-template-columns: 80pt 1fr 66pt; gap: 9pt; padding: 4pt 0; border-bottom: .75pt dotted ${c.lineDot};
          align-items: center; break-inside: avoid; }
    .dl b { white-space: nowrap; }
    .dl .n { grid-column: 2 / -1; color: ${c.note}; white-space: pre-wrap; font-size: 10pt; }
    .st { font-size: 7.5pt; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; border-radius: 3pt; padding: 1.5pt 4pt;
          text-align: center; background: ${c.chipBg}; color: ${c.chipInk}; }
    .st.lim { background: ${c.limBg}; color: ${c.limInk}; }
    .st.done { background: ${c.doneBg}; color: ${c.doneInk}; }
    .pfoot { margin-top: 22pt; padding-top: 6pt; border-top: .75pt solid ${c.lineSolid}; display: flex; justify-content: space-between;
             gap: 10pt; font-size: 8.5pt; color: ${c.foot}; }
  `
}

// ── section rendering ──────────────────────────────────────
const renderRow = (label: string, value: string, long: boolean): string => {
  const v = escapeHtml(plainText(value)) || '—'
  return `<div class="row${long ? ' full' : ''}"><span class="k">${escapeHtml(label)}</span><span class="v">${v}</span></div>`
}
const secHead = (label: string): string => `<div class="sec-h"><i></i>${escapeHtml(label)}</div>`

const renderCaseSections = (flat: Record<string, unknown>, opts: CasePdfOptions): string => {
  const parts: string[] = []
  for (const group of CASE_SECTION_GROUPS) {
    if (!opts.sections.has(group.key)) continue
    // Summary → the curated subset; full → every field. copyOnly fields are
    // control flags (e.g. a location-mode toggle) — never shown as a row.
    const fields = (opts.detailLevel === 'summary'
      ? group.fields.filter((f) => group.summaryKeys.includes(f.key))
      : group.fields
    ).filter((f) => !f.copyOnly)
    const rows: string[] = []
    for (const f of fields) {
      const raw = flat[f.key]
      if (opts.hideEmpty && isEmptyValue(raw, f.key)) continue
      rows.push(renderRow(f.label, formatValue(raw), !!f.long))
    }
    // Skip a section that ended up with no rows (e.g. hideEmpty removed all).
    if (rows.length === 0) continue
    parts.push(`<div class="sec">${secHead(group.label)}<div class="kv">${rows.join('')}</div></div>`)
  }
  return parts.join('')
}

const renderWitnesses = (witnesses: Witness[]): string => {
  if (witnesses.length === 0) return ''
  const items = witnesses.map((w) => `
    <div class="kv">
      ${renderRow('Name', w.name, false)}
      ${renderRow('Phone', w.phone, false)}
      ${renderRow('Email', w.email, false)}
      ${renderRow('Statement', w.statement, true)}
    </div>`).join('')
  return `<div class="sec">${secHead(`Witnesses · ${witnesses.length}`)}${items}</div>`
}

const isLimitation = (d: CaseDeadline): boolean => /limitation/i.test(d.deadlineType?.name || '')
const renderDeadlines = (deadlines: CaseDeadline[]): string => {
  if (deadlines.length === 0) return ''
  const sorted = [...deadlines].sort((a, b) => (ymd(a.dueDate) ?? '').localeCompare(ymd(b.dueDate) ?? ''))
  const items = sorted.map((d) => {
    const chip = d.completed
      ? '<span class="st done">Done</span>'
      : isLimitation(d) ? '<span class="st lim">Limitation</span>' : '<span class="st">Open</span>'
    const notes = plainText(d.notes)
    return `<div class="dl"><b>${escapeHtml(readableDate(d.dueDate) ?? '—')}</b><span>${escapeHtml(d.deadlineType?.name || '—')}</span>${chip}${
      notes ? `<span class="n">${escapeHtml(notes)}</span>` : ''}</div>`
  }).join('')
  return `<div class="sec">${secHead(`Deadlines · ${deadlines.length}`)}${items}</div>`
}

// ── the document ───────────────────────────────────────────
// Takes a FLAT case record (the flattenCase key set PLUS id/createdAt, and
// optionally matterNumber + assignedTo) — so it runs from the case page AND
// from a list row's fetched RawCase.
export const buildCasePdfHtml = (flat: Record<string, unknown>, opts: CasePdfOptions): string => {
  const str = (k: string): string => (flat[k] === null || flat[k] === undefined ? '' : String(flat[k]))
  const title = caseName({ caseName: str('caseName'), clientForename: str('clientForename'), clientSurname: str('clientSurname'), defendantName: str('defendantName') })
  const ref = sharedCaseRef({ id: str('id'), createdAt: str('createdAt'), clientSurname: str('clientSurname') })
  const now = new Date()
  const generated = `${String(now.getDate()).padStart(2, '0')} ${MONTHS[now.getMonth()]} ${now.getFullYear()}, ${
    String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  // Status mirrors the case-header rule: Closed (closeDate) → Open (setup finalised) → Draft.
  const isClosed = str('closeDate').trim() !== ''
  const status = isClosed ? 'Closed' : flat.setupFinalised === true ? 'Open' : 'Draft'

  const showFirm = opts.letterhead && !!opts.firmName
  const firmLabel = showFirm ? (opts.firmName as string) : 'Casevo'

  // Parties · matter · fee earner. (Bug fix: the old subtitle printed
  // "Matter {caseRef}" — the REFERENCE labelled as the matter number.)
  const client = `${str('clientForename')} ${str('clientSurname')}`.trim()
  const defendant = str('defendantName').trim()
  const assignee = flat.assignedTo as { firstName?: string; lastName?: string } | null | undefined
  const feeEarner = assignee ? `${assignee.firstName ?? ''} ${assignee.lastName ?? ''}`.trim() : ''
  const subParts = [
    client || defendant ? `${client || 'Client'}${defendant ? ` v ${defendant}` : ''}` : '',
    flat.matterNumber !== null && flat.matterNumber !== undefined && str('matterNumber') !== '' ? `Matter ${str('matterNumber')}` : '',
    feeEarner ? `Fee earner ${feeEarner}` : '',
  ].filter(Boolean)

  // 4 key facts — same rules as the case hero (caseFacts.ts).
  const deadlines = opts.deadlines ?? []
  // ★ (Audit) The shared date rule (textDateToIso — ISO or a UK D/M/YYYY), as
  // the hero: a "15/03/2022" accident date gives the same estimate here.
  const accYmd = textDateToIso(typeof flat.accidentDate === 'string' ? flat.accidentDate : null)
  // Same row rule as the hero / Overview / Deadlines (open first, then earliest).
  const limDl = limitationRowOf(deadlines)
  // No deadline → THE estimate (utils/limitationRules: a child runs from their
  // 18th birthday; no DOB → accident + 3y, labelled so), with THE case DOB.
  // (Audit fix 1) Both dates of birth when they differ → the earlier estimate.
  const caseDob = caseDobOf(str('clientDateOfBirth'), (flat.client as { dateOfBirth?: string | null } | null | undefined)?.dateOfBirth).limitationDob
  const estimate = limDl && ymd(limDl.dueDate) ? null : estimatedLimitation(accYmd, caseDob, claimFactsOf(claimOfRaw(flat), flat.protectedParty === true))
  const limYmd = (limDl && ymd(limDl.dueDate)) || estimate?.ymd || null
  const limEst = estimate ? estimateParen(estimate.basis) : null
  // (B2b) Child sexual abuse confirmed as having no time limit (s.11ZA).
  const limFacts = claimFactsOf(claimOfRaw(flat), flat.protectedParty === true)
  const noLimit = !limYmd && !limFacts.overrideDate && noTimeLimitApplies(accYmd, caseDob, limFacts)
  // (B3b) "Other" prints with its description, e.g. "Other: burns to the forearm".
  const otherDetails = str('injuryOtherDetails').trim()
  const injuries = INJURY_SHORT.filter(([k]) => flat[k] === true).map(([k, l]) => (k === 'injuryOther' && otherDetails ? `Other: ${otherDetails}` : l))
  const injuryText = injuries.length === 0 ? '—' : injuries.length <= 2 ? injuries.join(' · ') : `${injuries.slice(0, 2).join(' · ')} +${injuries.length - 2}`
  const nextDl = deadlines
    .filter((d) => !d.completed && !!ymd(d.dueDate))
    .sort((a, b) => (ymd(a.dueDate) ?? '').localeCompare(ymd(b.dueDate) ?? ''))[0]
  const fact = (k: string, v: string, extra = ''): string =>
    `<div><div class="k">${escapeHtml(k)}</div><div class="v">${escapeHtml(v)}${extra}</div></div>`

  // The claim type pill — left out when no type is set (this PDF can go to
  // the client or counsel; "not set" is internal).
  const claimLabel = claimTypeLabel(flat.claimType)
  const claimPill = claimLabel ? `<span>${escapeHtml(claimLabel)}</span>` : ''
  const header = `
    <div class="lh">
      ${showFirm && opts.logoDataUri && /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/.test(opts.logoDataUri)
        ? `<img class="logo" src="${opts.logoDataUri}" alt="${escapeHtml(firmLabel)}" />`
        : '<div class="mark">C</div>'}
      <div class="firm">${escapeHtml(firmLabel)}<small>Personal injury · Case summary</small></div>
      <div class="gen">Generated <b>${escapeHtml(generated)}</b>${opts.generatedBy ? `<br>by ${escapeHtml(opts.generatedBy)}` : ''}</div>
    </div>
    <div class="band">
      <div class="pills">${claimPill}<span class="m">${escapeHtml(ref)}</span><span>${escapeHtml(status)}</span></div>
      <h1>${escapeHtml(title)}</h1>
      ${subParts.length ? `<div class="sub">${escapeHtml(subParts.join(' · '))}</div>` : ''}
      <div class="facts">
        ${fact('Accident', readableDate(flat.accidentDate) ?? '—')}
        ${fact('Limitation', limYmd ? (readableDate(limYmd) as string) : noLimit ? 'No time limit (s.11ZA)' : '—', limEst ? ` <span class="e">${escapeHtml(limEst)}</span>` : '')}
        ${fact('Injuries', injuryText)}
        ${fact('Next deadline', nextDl ? (readableDate(nextDl.dueDate) as string) : '—')}
      </div>
    </div>`

  const sections = [
    renderCaseSections(flat, opts),
    opts.includeWitnesses ? renderWitnesses(opts.witnesses ?? []) : '',
    opts.includeDeadlines ? renderDeadlines(deadlines) : '',
  ].join('')

  // Running header (pages 2+) + footer (every page) — see styleBlock.
  const furniture: Furniture = {
    headLeft: `${firmLabel} · ${ref}`,
    headRight: title,
    footLeft: `Confidential${showFirm ? ` — prepared for ${opts.firmName as string}` : ''} · ${ref}`,
  }

  // In-flow footer: shown on screen only (print uses the margin boxes, which
  // repeat on every page). Bug fix: the old footer read "Generated by Casevo · Confidential" + "Casevo".
  const footer = `
    <div class="pfoot">
      <span>Confidential${showFirm ? ` — prepared for ${escapeHtml(opts.firmName as string)}` : ''} · ${escapeHtml(ref)}</span>
      <span>Generated by Casevo</span>
    </div>`

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${escapeHtml(title)} — Case summary</title>
    <style>${styleBlock(opts.dark, furniture, documentTheme(opts.accentColour, opts.secondaryColour), clientDocumentFonts(opts.headingFont, opts.bodyFont))}</style></head>
    <body><div class="pdf">${header}${sections}${footer}</div></body></html>`
}

// The longest printing waits for fonts + images before printing anyway.
export const PRINT_WAIT_CAP_MS = 5000
// Resolves once the document's fonts and images are in (loaded or failed).
export const documentSettled = async (doc: Document): Promise<void> => {
  void doc.body?.offsetHeight // force style + layout → the font loads start now
  const fonts = (doc as Document & { fonts?: { ready: Promise<unknown> } }).fonts
  const images = Array.from(doc.images).filter((img) => !img.complete).map((img) => new Promise<void>((resolve) => {
    img.addEventListener('load', () => resolve(), { once: true })
    img.addEventListener('error', () => resolve(), { once: true })
  }))
  await Promise.all([fonts ? fonts.ready.then(() => undefined, () => undefined) : Promise.resolve(), ...images])
}

// Open the summary in a REAL new tab and print from it (more reliable than a
// hidden iframe). Returns false if the browser BLOCKED the pop-up.
//
// CRITICAL: this must be called SYNCHRONOUSLY from the user's click (no await
// before it) or the pop-up is blocked. The call chain (click → handleGenerate
// → exportCasePdf → printCasePdf) is fully synchronous.
export const printCasePdf = (html: string): boolean => {
  const win = window.open('', '_blank')
  if (!win) return false // pop-up blocked
  win.document.open()
  win.document.write(html)
  win.document.close()
  // Print once the document is COMPLETE (batch 2): layout forced (that is
  // what asks for the web fonts), then document.fonts.ready AND every image
  // (logo, signature) loaded or failed — capped at PRINT_WAIT_CAP_MS so a
  // stuck font or image never stalls printing; the text never prints in a
  // fallback font while the bundled one is still on its way.
  // We do NOT auto-close — the user keeps the tab (re-print / save from it).
  win.setTimeout(() => {
    let done = false
    const doPrint = (): void => {
      if (done) return
      done = true
      try {
        win.focus()
        win.print()
      } catch {
        /* print blocked — the tab is open, the user can print manually */
      }
    }
    win.setTimeout(doPrint, PRINT_WAIT_CAP_MS)
    void documentSettled(win.document).then(doPrint, doPrint)
  }, 50)
  return true
}

// Convenience: build + print in one call. Returns false if the pop-up was
// blocked (so the dialog can surface a clear message).
export const exportCasePdf = (flat: Record<string, unknown>, opts: CasePdfOptions): boolean =>
  printCasePdf(buildCasePdfHtml(flat, opts))
